import SearchButtonIcon from 'assets/svg/search/search-white.svg'
import Flex from 'common/components/Flex'
import Loader from 'common/components/Loader'
import Overlay from 'common/components/Overlay'
import theme from 'common/theme'
import { useEffect, useRef, useState } from 'react'
import RenderComponent from 'services/User/components/RenderComponent'
import styled from 'styled-components'

interface IGlobalSearchbar {
  isOpen: boolean
  setIsOpen: (boolean) => void
  children: JSX.Element
  handleTyping: (any) => void
  keywords: string
  onShowSuggestion: () => void
  keyboardNavigation: (any) => void
  isLoadingSearch: boolean
  onSearchClick: () => void
}

const PrependSearchIcon = styled(SearchButtonIcon)`
  & path {
    fill: ${theme.colors.gray};
  }
`

const PrependWrapper = styled(Flex)`
  position: absolute;
  height: 100%;
  align-items: center;
  justify-content: center;
`

const StyledTextInput = styled.input`
  flex-grow: 1;
  padding: 12px 0px 12px 50px;
  border: 1px solid ${theme.colors.grayLight};
  font-size: 14px;
  box-shadow: none;
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;

  &: focus {
    outline: none;
    border: solid 1px ${theme.colors.orange};
  }
`

const GlobalSearchBarContainer = styled.div`
  position: fixed;
  left: 50%;
  transform: translate(-50%, 0);
  width: 790px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 15%;
`

const GlobalSearchBarWrapper = styled(Flex)`
  position: relative;
  width: 790px;
`

const SearchButton = styled.div`
  width: 80px;
  background-color: ${theme.colors.orange};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
`

const SearchResultOverlay = styled(Flex)`
  width: 790px;
  box-sizing: border-box;
  background-color: ${theme.colors.white};
  border-bottom-right-radius: ${theme.radius.lg};
  border-bottom-left-radius: ${theme.radius.lg};
`
const ButtonLoaderWrapper = styled.span`
  margin: 0 auto;
`

const GlobalSearchBar = ({
  isOpen,
  setIsOpen,
  children,
  handleTyping,
  keywords,
  onShowSuggestion,
  keyboardNavigation,
  isLoadingSearch,
  onSearchClick,
}: IGlobalSearchbar): JSX.Element => {
  const [isOpenResult, setIsOpenResult] = useState(false)

  const searchInput = useRef(null)

  useEffect(() => {
    if (isOpen) {
      searchInput.current.focus()
      onShowSuggestion()
    }
  }, [isOpen])

  const CloseGlobalSearchBar = () => {
    setIsOpen(false)
    setIsOpenResult(false)
  }

  return (
    <Overlay
      zIndex={`${theme.zIndex.modal}`}
      isActive={isOpen}
      onOverlayClick={CloseGlobalSearchBar}
    >
      <RenderComponent isShow={isOpen}>
        <GlobalSearchBarContainer
          onClick={(e) => e.stopPropagation()}
        >
          <GlobalSearchBarWrapper data-testid="global-search-bar">
            <PrependWrapper margin="0 14px 0 18px">
              <PrependSearchIcon />
            </PrependWrapper>

            <StyledTextInput
              onFocus={() => setIsOpenResult(true)}
              data-testid="search-bar-input"
              value={keywords}
              onChange={handleTyping}
              ref={searchInput}
              onKeyDown={keyboardNavigation}
            />

            <SearchButton
              onClick={onSearchClick}
              data-testid="search-button"
            >
              {!isLoadingSearch ? (
                <SearchButtonIcon />
              ) : (
                <ButtonLoaderWrapper>
                  <Loader
                    radius="60px"
                    primaryColor={theme.colors.white}
                    secondaryColor={theme.colors.white}
                  />
                </ButtonLoaderWrapper>
              )}
            </SearchButton>
          </GlobalSearchBarWrapper>

          <RenderComponent isShow={isOpenResult}>
            <SearchResultOverlay>
              {isOpenResult && children}
            </SearchResultOverlay>
          </RenderComponent>
        </GlobalSearchBarContainer>
      </RenderComponent>
    </Overlay>
  )
}

export default GlobalSearchBar
